/**
 * Ticketing Integration Layer
 * Connects ticketing API service to Zustand stores
 */
import { toast } from 'sonner';
import i18n from 'i18next';
import { useTicketStore } from '../../store/ticket.store.js';
import { useTicketTemplateStore } from '../../store/ticket-template.store.js';
import {
    ticketApi,
    ticketTemplateApi,
    ticketCategoryApi,
    ticketSlaApi,
    ticketConfigApi,
    ticketRecurrenceApi
} from './ticket.api.js';
import { TICKET_API_READ_ONLY_CODE, ticketMutationToastMessage } from '../ticketing/ticket-api-error.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';

// ─────────────────────────────────────────────────────────────────────────────
// Ticket Integration
// ─────────────────────────────────────────────────────────────────────────────
export class TicketIntegration {
    async fetchTickets(params = {}) {
        const store = useTicketStore.getState();
        store.setLoading(true);
        try {
            const merged = { ...store.searchParams, ...params };
            store.setSearchParams(params);
            const res = await ticketApi.getAll(merged);
            store.setTickets(res.data || []);
            if (res.pagination) store.setPaginationData(res.pagination);
            return { success: true, data: res.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotLoadTickets', { defaultValue: 'Could not load tickets' }));
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchTicketById(id) {
        const store = useTicketStore.getState();
        store.setDetailLoading(true);
        try {
            const res = await ticketApi.getById(id);
            store.setSelectedTicket(res.data);
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:ticketNotFound', { defaultValue: 'Ticket not found' })));
            return { success: false, error: ticketMutationToastMessage(err, i18n.t('integrationToasts:requestFailed', { defaultValue: 'Request failed' })) };
        } finally {
            store.setDetailLoading(false);
        }
    }

    /** Global/tenant party template for ticket list columns (ADR-001). */
    async fetchTicketPartyTemplate(params = {}) {
        try {
            const res = await ticketApi.getPartyTemplate(params);
            return { success: res.success !== false, data: res.data ?? res.partyTemplate ?? null };
        } catch {
            return { success: false, data: null };
        }
    }

    /** Party instance for ticket (ADR-001); errors are silent — legacy fields remain fallback. */
    async fetchTicketParty(ticketId) {
        if (!ticketId) return { success: false, data: null, partyTemplate: null };
        try {
            const res = await ticketApi.getParty(ticketId);
            return {
                success: res.success !== false,
                data: res.data ?? null,
                partyTemplate: res.partyTemplate ?? null,
            };
        } catch {
            return { success: false, data: null, partyTemplate: null };
        }
    }

    async syncTicketParty(ticketId, payload, options = {}) {
        const { silentSuccess = false } = options;
        try {
            const res = await ticketApi.syncParty(ticketId, payload);
            if (!silentSuccess) {
                toast.success(
                    i18n.t('ticketingDetail:ownership.saveSuccess', {
                            defaultValue: 'Roles saved',
                        }),
                );
            }
            return { success: true, data: res.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotSaveRoles', { defaultValue: 'Could not save roles' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async fetchMyPending() {
        const store = useTicketStore.getState();
        try {
            const res = await ticketApi.getMyPending();
            store.setMyPendingTickets(res.data || []);
            return { success: true, data: res.data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }

    async fetchPool(params = {}) {
        const store = useTicketStore.getState();
        try {
            const res = await ticketApi.getPool(params);
            store.setPoolTickets(res.data || []);
            return { success: true, data: res.data };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }

    async fetchStats() {
        const store = useTicketStore.getState();
        try {
            const res = await ticketApi.getStats();
            store.setStats(res.data);
            return { success: true, data: res.data };
        } catch {
            return { success: false };
        }
    }

    async createTicket(data) {
        const store = useTicketStore.getState();
        store.setLoading(true);
        try {
            const res = await ticketApi.create(data);
            toast.success(i18n.t('integrationToasts:ticketCreated', { defaultValue: 'Ticket created' }));
            store.closeCreateForm();
            await this.fetchTickets();
            return { success: true, data: res.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotCreateTicket', { defaultValue: 'Could not create ticket' }));
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async updateTicket(id, data) {
        try {
            const res = await ticketApi.update(id, data);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:ticketUpdated', { defaultValue: 'Ticket updated' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:updateFailed', { defaultValue: 'Update failed' })));
            return { success: false };
        }
    }

    async updateItsmLinks(id, data) {
        try {
            const res = await ticketApi.postItsmLinks(id, data);
            useTicketStore.getState().setSelectedTicket(res.data);
            return { success: true, data: res.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotUpdateItsmLinks', { defaultValue: 'Could not update ITSM links' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async updateItsmCatalog(id, data) {
        try {
            const res = await ticketApi.patchItsmCatalog(id, data);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('ticketingDetail:itsm.catalogSaved', { defaultValue: 'Service catalog updated' }));
            return { success: true, data: res.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('ticketingDetail:itsm.catalogSaveFailed', { defaultValue: 'Could not update service catalog' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async updateTicketTasks(id, tasks) {
        try {
            const res = await ticketApi.putTicketTasks(id, { tasks });
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:taskListUpdated', { defaultValue: 'Task list updated' }));
            return { success: true, data: res.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotUpdateTaskList', { defaultValue: 'Could not update task list' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async transitionTicketTask(ticketId, taskId, payload = {}) {
        try {
            const res = await ticketApi.transitionTicketTask(ticketId, taskId, payload);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:taskStatusUpdated', { defaultValue: 'Task status updated' }));
            return { success: true, data: res.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotUpdateTaskStatus', { defaultValue: 'Could not update task status' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async patchTicketTask(ticketId, taskId, payload = {}) {
        try {
            const res = await ticketApi.patchTicketTask(ticketId, taskId, payload);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:taskUpdated', { defaultValue: 'Task updated' }));
            return { success: true, data: res.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotUpdateTask', { defaultValue: 'Could not update task' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async deleteTicketTask(ticketId, taskId) {
        try {
            const res = await ticketApi.deleteTicketTask(ticketId, taskId);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:taskDeleted', { defaultValue: 'Task deleted' }));
            return { success: true, data: res.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotDeleteTask', { defaultValue: 'Could not delete task' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async listSubtaskComments(ticketId, taskId) {
        try {
            const res = await ticketApi.listSubtaskComments(ticketId, taskId);
            return { success: true, data: res.data || [] };
        } catch (err) {
            return { success: false, error: err.message, data: [] };
        }
    }

    async addSubtaskComment(ticketId, taskId, content) {
        try {
            await ticketApi.addSubtaskComment(ticketId, taskId, { content });
            toast.success(i18n.t('integrationToasts:commentAdded', { defaultValue: 'Comment added' }));
            return { success: true };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotAddComment', { defaultValue: 'Could not add comment' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async submitTicket(id) {
        try {
            const res = await ticketApi.submit(id);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:ticketSentForReview', { defaultValue: 'Ticket sent for review' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotSend', { defaultValue: 'Could not send' })));
            return { success: false };
        }
    }

    async assignTicket(id, data) {
        try {
            const res = await ticketApi.assign(id, data);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(
                i18n.t('ticketingDetail:messages.assignSuccess', { defaultValue: 'Ticket assigned' }),
            );
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:assignmentFailed', { defaultValue: 'Assignment failed' })));
            return { success: false };
        }
    }

    async patchExecutionFormData(id, data) {
        try {
            const res = await ticketApi.patchFormData(id, data);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(
                i18n.t('ticketingDetail:messages.formDataSaveSuccess', { defaultValue: 'Form data saved' }),
            );
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotSaveForm', { defaultValue: 'Could not save form' })));
            return { success: false };
        }
    }

    async claimTicket(id) {
        try {
            const res = await ticketApi.claim(id);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:taskClaimed', { defaultValue: 'Task claimed' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotClaimTask', { defaultValue: 'Could not claim task' })));
            return { success: false };
        }
    }

    async acceptAssignment(id) {
        try {
            const res = await ticketApi.acceptAssignment(id);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:taskAccepted', { defaultValue: 'Task accepted' }));
            await this.fetchMyPending();
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotAcceptTask', { defaultValue: 'Could not accept task' })));
            return { success: false };
        }
    }

    async rejectAssignment(id, data) {
        try {
            const res = await ticketApi.rejectAssignment(id, data);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:taskRejected', { defaultValue: 'Task rejected' }));
            await this.fetchMyPending();
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotRejectTask', { defaultValue: 'Could not reject task' })));
            return { success: false };
        }
    }

    async resolveTicket(id, data) {
        try {
            const res = await ticketApi.resolve(id, data);
            if (res.data) useTicketStore.getState().setSelectedTicket(res.data);
            if (res?.pending) {
                toast.info(
                    i18n.t('ticketingDetail:messages.resolvePending', {
                            defaultValue: 'Resolve action submitted to approval workflow. You will be notified of the result.',
                        }),
                );
                return { success: true, pending: true, data: res.data };
            }
            toast.success(i18n.t('integrationToasts:ticketClosedAsResolved', { defaultValue: 'Ticket closed as resolved' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:resolveFailed', { defaultValue: 'Resolve failed' })));
            return { success: false };
        }
    }

    async closeTicket(id, data) {
        try {
            const res = await ticketApi.close(id, data);
            if (res.data) useTicketStore.getState().setSelectedTicket(res.data);
            if (res?.pending) {
                toast.info(
                    i18n.t('ticketingDetail:messages.closePending', {
                            defaultValue: 'Close action submitted to approval workflow. You will be notified of the result.',
                        }),
                );
                return { success: true, pending: true, data: res.data };
            }
            toast.success(i18n.t('integrationToasts:ticketClosed', { defaultValue: 'Ticket closed' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:closeFailed', { defaultValue: 'Close failed' })));
            return { success: false };
        }
    }

    async cancelTicket(id, data) {
        try {
            const res = await ticketApi.cancel(id, data);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:ticketCancelled', { defaultValue: 'Ticket cancelled' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:cancelFailed', { defaultValue: 'Cancel failed' })));
            return { success: false };
        }
    }

    async getReportUrl(id) {
        try {
            const res = await ticketApi.getReportUrl(id);
            return { success: true, data: res.data };
        } catch {
            return { success: false };
        }
    }

    async addWatcher(id, data = {}) {
        try {
            const res = await ticketApi.addWatcher(id, data);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:nowFollowing', { defaultValue: 'Now following' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotFollow', { defaultValue: 'Could not follow' })));
            return { success: false };
        }
    }

    async removeWatcher(id, data = {}) {
        try {
            const res = await ticketApi.removeWatcher(id, data);
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:unfollowed', { defaultValue: 'Unfollowed' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotUnfollow', { defaultValue: 'Could not unfollow' })));
            return { success: false };
        }
    }

    async bulkAction(data) {
        try {
            const res = await ticketApi.bulkAction(data);
            const d = res?.data || res;
            const ok = d?.success ?? 0;
            const fail = d?.failed ?? 0;
            const errs = Array.isArray(d?.errors) ? d.errors : [];
            const finalizedSkips = errs.filter((e) => e?.code === TICKET_API_READ_ONLY_CODE).length;
            if (fail > 0) {
                let line = i18n.t('ticketingDetail:messages.bulkUpdatePartial', {
                    ok,
                    fail,
                    defaultValue: `${ok} records updated, ${fail} failed`,
                });
                if (finalizedSkips > 0) {
                    line = `${line}. ${i18n.t('readOnly.bulkSkippedFinalizedHint', { ns: 'ticketingDetail', count: finalizedSkips })}`;
                }
                toast.warning(line);
            } else {
                toast.success(i18n.t('integrationToasts:nRecordsUpdated', {
                    count: ok,
                    defaultValue: `${ok} records updated`,
                }));
            }
            await this.fetchTickets();
            return { success: true, data: d };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:bulkOperationFailed', { defaultValue: 'Bulk operation failed' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async delete(id) {
        try {
            const res = await ticketApi.delete(id);
            if (res?.pending) {
                toast.info(
                    i18n.t('ticketingList:messages.deletePending', {
                            defaultValue: 'Delete action submitted to approval workflow. You will be notified of the result.',
                        }),
                );
            } else {
                toast.success(
                    i18n.t('ticketingList:messages.deleteSuccess', { defaultValue: 'Ticket deleted' }),
                );
            }
            await this.fetchTickets();
            await this.fetchStats();
            return { success: true, pending: Boolean(res?.pending) };
        } catch (err) {
            const msg = ticketMutationToastMessage(
                err,
                i18n.t('ticketingList:messages.deleteError', { defaultValue: 'Could not delete ticket' }),
            );
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async batchDelete(ids) {
        if (!Array.isArray(ids) || ids.length === 0) {
            return { success: false };
        }
        try {
            const res = await ticketApi.batchDelete(ids);
            const deleted = res?.deletedCount ?? 0;
            const pending = res?.pendingCount ?? 0;
            const failed = res?.failed ?? 0;
            if (failed > 0 || (pending > 0 && deleted > 0)) {
                toast.warning(i18n.t('ticketingList:messages.bulkDeletePartial', {
                    defaultValue: '{{deleted}} tickets deleted, {{failed}} failed',
                    deleted,
                    failed,
                }));
            } else if (pending > 0) {
                toast.info(
                    i18n.t('ticketingList:messages.bulkDeletePending', {
                            defaultValue: '{{count}} tickets submitted to approval workflow',
                            count: pending,
                        }),
                );
            } else {
                toast.success(
                    i18n.t('ticketingList:messages.bulkDeleteSuccess', {
                            defaultValue: '{{count}} tickets deleted',
                            count: deleted,
                        }),
                );
            }
            await this.fetchTickets();
            await this.fetchStats();
            return { success: deleted > 0 || pending > 0, data: res };
        } catch (err) {
            const msg = ticketMutationToastMessage(
                err,
                i18n.t('ticketingList:messages.deleteError', { defaultValue: 'Could not delete ticket' }),
            );
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async createSubTicket(parentId, data) {
        try {
            const res = await ticketApi.createSubTicket(parentId, data);
            toast.success(i18n.t('integrationToasts:subtaskCreated', { defaultValue: 'Subtask created' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotCreateSubtask', { defaultValue: 'Could not create subtask' })));
            return { success: false };
        }
    }

    async mergeTickets(targetId, sourceTicketId) {
        try {
            const res = await ticketApi.merge(targetId, { sourceTicketId });
            useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('integrationToasts:ticketsMerged', { defaultValue: 'Tickets merged' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('integrationToasts:mergeFailed', { defaultValue: 'Merge failed' })));
            return { success: false };
        }
    }

    async uploadAttachment(id, file) {
        try {
            const res = await ticketApi.uploadAttachment(id, file);
            if (res.data) useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('ticketingDetail:attachments.toasts.uploadSuccess', { defaultValue: 'Attachment uploaded' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('ticketingDetail:attachments.toasts.uploadError', { defaultValue: 'Could not upload attachment' })));
            return { success: false };
        }
    }

    async deleteAttachment(id, attachmentId) {
        try {
            const res = await ticketApi.deleteAttachment(id, attachmentId);
            if (res.data) useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('ticketingDetail:attachments.toasts.deleteSuccess', { defaultValue: 'Attachment deleted' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('ticketingDetail:attachments.toasts.deleteError', { defaultValue: 'Could not delete attachment' })));
            return { success: false };
        }
    }

    async addRelatedTicket(id, relatedTicketId) {
        try {
            const res = await ticketApi.addRelatedTicket(id, relatedTicketId);
            if (res.data) useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('ticketingDetail:links.toasts.relatedAdded', { defaultValue: 'Related ticket linked' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('ticketingDetail:links.toasts.relatedAddError', { defaultValue: 'Could not link related ticket' })));
            return { success: false };
        }
    }

    async removeRelatedTicket(id, relatedId) {
        try {
            const res = await ticketApi.removeRelatedTicket(id, relatedId);
            if (res.data) useTicketStore.getState().setSelectedTicket(res.data);
            toast.success(i18n.t('ticketingDetail:links.toasts.relatedRemoved', { defaultValue: 'Related ticket unlinked' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(ticketMutationToastMessage(err, i18n.t('ticketingDetail:links.toasts.relatedRemoveError', { defaultValue: 'Could not unlink related ticket' })));
            return { success: false };
        }
    }

    async searchSimilar(query) {
        try {
            const res = await ticketApi.searchSimilar(query);
            const list = Array.isArray(res?.data) ? res.data : [];
            return { success: true, data: list };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotFetchSimilarTickets', { defaultValue: 'Could not fetch similar tickets' }));
            toast.error(msg);
            return { success: false, data: [], error: msg };
        }
    }

    notifyTicketNumberCopied() {
        toast.success(
            i18n.t('ticketingDetail:actions.numberCopied', { defaultValue: 'Ticket number copied' }),
        );
    }

    notifySimilarQueryTooShort() {
        toast.warning(
            i18n.t('ticketingDetail:advanced.similarMinLength', {
                defaultValue: 'Enter at least 3 characters for similar search',
            }),
        );
    }

    notifyMergeSourceNotFound() {
        toast.error(
            i18n.t('ticketingDetail:advanced.mergeSourceNotFound', {
                defaultValue: 'Source ticket not found',
            }),
        );
    }

    notifyMergeSelf() {
        toast.error(
            i18n.t('ticketingDetail:advanced.mergeSelf', {
                defaultValue: 'A ticket cannot be merged with itself',
            }),
        );
    }

    async resolveMergeSourceTicketId(raw) {
        const s = String(raw || '').trim();
        if (!s) return { success: true, data: null };
        if (/^[a-fA-F0-9]{24}$/.test(s)) {
            return { success: true, data: s };
        }
        try {
            const body = await ticketApi.getAll({ search: s, limit: 30, page: 1 });
            const list = body?.data || [];
            const exactNum = list.find(
                (x) => (x.ticketNumber || '').trim().toUpperCase() === s.toUpperCase(),
            );
            if (exactNum) {
                return { success: true, data: String(exactNum._id || exactNum.id) };
            }
            const loose = list.find((x) =>
                String(x.ticketNumber || '').toLowerCase().includes(s.toLowerCase()),
            );
            return { success: true, data: loose ? String(loose._id || loose.id) : null };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotSearchTickets', { defaultValue: 'Could not search tickets' }));
            toast.error(msg);
            return { success: false, data: null, error: msg };
        }
    }

    async exportTickets(params = {}) {
        try {
            const body = await ticketApi.exportTickets({ ...params, format: 'json' });
            const rows = Array.isArray(body?.data) ? body.data : [];
            const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `tickets-export-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(i18n.t('integrationToasts:exportDownloaded'));
            return { success: true, data: rows };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:exportFailed'));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getRelatedActions(ticketId, params = {}) {
        try {
            const response = await ticketApi.getRelatedActions(ticketId, params);
            return {
                success: true,
                data: response.data,
                meta: response.pagination || response.meta || {},
            };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotLoadLinkedActions', { defaultValue: 'Could not load linked actions' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async getLinkableActions(ticketId, params = {}) {
        try {
            const response = await ticketApi.getLinkableActions(ticketId, params);
            return {
                success: true,
                data: response.data,
                meta: response.pagination || response.meta || {},
            };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }

    async linkActions(ticketId, actionIds, opts = {}) {
        try {
            const response = await ticketApi.linkActions(ticketId, actionIds, opts);
            if (!opts.silent) {
                toast.success(i18n.t('integrationToasts:actionLinked', { defaultValue: 'Action linked' }));
            }
            return { success: true, data: response.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotLinkActions', { defaultValue: 'Could not link actions' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkActions(ticketId, actionIds, opts = {}) {
        try {
            const response = await ticketApi.unlinkActions(ticketId, actionIds);
            if (!opts.silent) {
                toast.success(i18n.t('integrationToasts:actionLinkRemoved', { defaultValue: 'Action link removed' }));
            }
            return { success: true, data: response.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotUnlinkAction', { defaultValue: 'Could not unlink action' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }

    async unlinkAction(ticketId, actionId, opts = {}) {
        return this.unlinkActions(ticketId, [actionId], opts);
    }

    async reorderActions(ticketId, { actionIds }) {
        try {
            const response = await ticketApi.reorderActions(ticketId, { actionIds });
            toast.success(i18n.t('integrationToasts:actionOrderUpdated', { defaultValue: 'Action order updated' }));
            return { success: true, data: response.data };
        } catch (err) {
            const msg = ticketMutationToastMessage(err, i18n.t('integrationToasts:couldNotUpdateActionOrder', { defaultValue: 'Could not update action order' }));
            toast.error(msg);
            return { success: false, error: msg };
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Ticket Template Integration
// ─────────────────────────────────────────────────────────────────────────────
export class TicketTemplateIntegration {
    async fetchTemplates(params = {}) {
        const store = useTicketTemplateStore.getState();
        store.setLoading(true);
        try {
            const { columnFilters, ...requestParams } = params;
            const res = await ticketTemplateApi.getAll({
                ...requestParams,
                ...mapColumnFiltersForQuery(columnFilters),
            });
            store.setTemplates(res.data || []);
            if (res.pagination) store.setPaginationData(res.pagination);
            store.setUniqueValues(res.uniqueValues || {});
            return { success: true, data: res.data, uniqueValues: res.uniqueValues || {} };
        } catch {
            toast.error(i18n.t('integrationToasts:couldNotLoadTemplates', { defaultValue: 'Could not load templates' }));
            return { success: false };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchTemplatesForCreate() {
        try {
            const res = await ticketTemplateApi.getForCreate();
            return { success: true, data: res.data || [] };
        } catch {
            toast.error(i18n.t('integrationToasts:couldNotLoadTemplates', { defaultValue: 'Could not load templates' }));
            return { success: false, data: [] };
        }
    }

    async fetchTemplateById(id) {
        const store = useTicketTemplateStore.getState();
        store.setLoading(true);
        try {
            const res = await ticketTemplateApi.getById(id);
            store.setSelectedTemplate(res.data);
            return { success: true, data: res.data };
        } catch {
            return { success: false };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchReportLinkOptions() {
        try {
            const res = await ticketTemplateApi.getReportLinkOptions();
            return { success: true, data: res.data || [] };
        } catch {
            toast.error(i18n.t('integrationToasts:couldNotLoadReportLinkOptions'));
            return { success: false, data: [] };
        }
    }

    async fetchWorkflowTemplateOptions(params = {}) {
        try {
            const res = await ticketTemplateApi.getWorkflowTemplateOptions(params);
            return { success: true, data: res.data || [] };
        } catch {
            toast.error(i18n.t('integrationToasts:couldNotLoadWorkflowTemplates', { defaultValue: 'Could not load workflow templates' }));
            return { success: false, data: [] };
        }
    }

    async createTemplate(data) {
        const store = useTicketTemplateStore.getState();
        try {
            const res = await ticketTemplateApi.create(data);
            toast.success(i18n.t('integrationToasts:templateCreated', { defaultValue: 'Template created' }));
            store.closeForm();
            await this.fetchTemplates();
            return { success: true, data: res.data };
        } catch {
            toast.error(i18n.t('integrationToasts:couldNotCreateTemplate', { defaultValue: 'Could not create template' }));
            return { success: false };
        }
    }

    async updateTemplate(id, data) {
        try {
            const res = await ticketTemplateApi.update(id, data);
            useTicketTemplateStore.getState().setSelectedTemplate(res.data);
            toast.success(i18n.t('integrationToasts:templateUpdated', { defaultValue: 'Template updated' }));
            return { success: true, data: res.data };
        } catch {
            toast.error(i18n.t('integrationToasts:updateFailed', { defaultValue: 'Update failed' }));
            return { success: false };
        }
    }

    async publishTemplate(id) {
        try {
            const res = await ticketTemplateApi.publish(id);
            toast.success(i18n.t('integrationToasts:templatePublished', { defaultValue: 'Template published' }));
            await this.fetchTemplates();
            return { success: true, data: res.data };
        } catch {
            toast.error(i18n.t('integrationToasts:publishFailed', { defaultValue: 'Publish failed' }));
            return { success: false };
        }
    }

    async archiveTemplate(id) {
        try {
            await ticketTemplateApi.archive(id);
            toast.success(i18n.t('integrationToasts:templateArchived', { defaultValue: 'Template archived' }));
            await this.fetchTemplates();
            return { success: true };
        } catch {
            toast.error(i18n.t('integrationToasts:archiveFailed', { defaultValue: 'Archive failed' }));
            return { success: false };
        }
    }

    async deleteTemplate(id) {
        try {
            await ticketTemplateApi.delete(id);
            toast.success(i18n.t('integrationToasts:templateDeleted', { defaultValue: 'Template deleted' }));
            await this.fetchTemplates({ limit: 100 });
            return { success: true };
        } catch {
            toast.error(i18n.t('integrationToasts:deleteFailed', { defaultValue: 'Delete failed' }));
            return { success: false };
        }
    }

    async createReportDesignerCapability(templateId, { silent = false } = {}) {
        try {
            const res = await ticketTemplateApi.createReportDesignerCapability(templateId);
            return { success: true, data: res.data };
        } catch {
            if (!silent) {
                toast.error(i18n.t('integrationToasts:couldNotOpenReportDesigner', {
                    defaultValue: 'Report designer could not be opened',
                }));
            }
            return { success: false, data: null };
        }
    }

    async fetchReportFields(templateId, { refresh = false } = {}) {
        const store = useTicketTemplateStore.getState();
        try {
            const params = refresh ? { refresh: 'true' } : {};
            const res = await ticketTemplateApi.getReportFields(templateId, params);
            store.setReportFields(res.data || []);

            if (!res.data || res.data.length === 0) {
                const toastKey = res.message === 'No report fields found'
                    ? 'integrationToasts:noReportFieldsFound'
                    : 'integrationToasts:reportingServerUnreachable';
                const defaultValue = res.message === 'No report fields found'
                    ? 'No report fields found'
                    : 'Could not reach the reporting server. Make sure the server is running.';
                toast.info(i18n.t(toastKey, { defaultValue }));
            }

            return { success: true, data: res.data || [], message: res.message };
        } catch {
            toast.error(i18n.t('integrationToasts:couldNotLoadReportFields'));
            return { success: false, data: [] };
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Simple CRUD Integrations (no dedicated store — use direct API)
// ─────────────────────────────────────────────────────────────────────────────
export class TicketCategoryIntegration {
    async fetchCategories(params = {}) {
        try {
            const res = await ticketCategoryApi.getAll(params);
            return { success: true, data: res.data || [] };
        } catch {
            return { success: false, data: [] };
        }
    }
    async create(data) {
        try { const res = await ticketCategoryApi.create(data); toast.success(i18n.t('integrationToasts:categoryCreated', { defaultValue: 'Category created' })); return { success: true, data: res.data }; }
        catch (err) { toast.error(getIntegrationErrorMessage(err)); return { success: false }; }
    }
    async update(id, data) {
        try { const res = await ticketCategoryApi.update(id, data); toast.success(i18n.t('integrationToasts:updated', { defaultValue: 'Updated' })); return { success: true, data: res.data }; }
        catch (err) { toast.error(getIntegrationErrorMessage(err)); return { success: false }; }
    }
    async delete(id) {
        try { await ticketCategoryApi.delete(id); toast.success(i18n.t('integrationToasts:deleted', { defaultValue: 'Deleted' })); return { success: true }; }
        catch (err) { toast.error(getIntegrationErrorMessage(err)); return { success: false }; }
    }
}

export class TicketSlaIntegration {
    async fetchSlas(params = {}) {
        try {
            const { columnFilters, ...requestParams } = params;
            const res = await ticketSlaApi.getAll({
                ...requestParams,
                ...mapColumnFiltersForQuery(columnFilters),
            });
            return { success: true, data: res.data || [], uniqueValues: res.uniqueValues || {} };
        }
        catch { return { success: false, data: [] }; }
    }
    async create(data) {
        try { const res = await ticketSlaApi.create(data); toast.success(i18n.t('integrationToasts:slaCreated', { defaultValue: 'SLA created' })); return { success: true, data: res.data }; }
        catch (err) { toast.error(getIntegrationErrorMessage(err)); return { success: false }; }
    }
    async update(id, data) {
        try { const res = await ticketSlaApi.update(id, data); toast.success(i18n.t('integrationToasts:updated', { defaultValue: 'Updated' })); return { success: true, data: res.data }; }
        catch (err) { toast.error(getIntegrationErrorMessage(err)); return { success: false }; }
    }
    async delete(id) {
        try { await ticketSlaApi.delete(id); toast.success(i18n.t('integrationToasts:deleted', { defaultValue: 'Deleted' })); return { success: true }; }
        catch (err) { toast.error(getIntegrationErrorMessage(err)); return { success: false }; }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Ticket Config Integration (tenant-level singleton)
// ─────────────────────────────────────────────────────────────────────────────
export class TicketConfigIntegration {
    async fetchConfig({ silent = false } = {}) {
        try {
            const res = await ticketConfigApi.get();
            return { success: true, data: res.data };
        } catch {
            if (!silent) {
                toast.error(i18n.t('integrationToasts:couldNotLoadSettings', { defaultValue: 'Could not load settings' }));
            }
            return { success: false, data: null };
        }
    }

    async updateConfig(data) {
        try {
            const res = await ticketConfigApi.update(data);
            toast.success(i18n.t('integrationToasts:settingsUpdated', { defaultValue: 'Settings updated' }));
            return { success: true, data: res.data };
        } catch {
            toast.error(i18n.t('integrationToasts:couldNotUpdateSettings', { defaultValue: 'Could not update settings' }));
            return { success: false };
        }
    }
}

export class TicketRecurrenceIntegration {
    async fetchAll(params = {}) {
        try {
            const res = await ticketRecurrenceApi.getAll(params);
            return { success: true, data: res.data || [] };
        } catch {
            return { success: false, data: [] };
        }
    }
    async create(data) {
        try {
            const res = await ticketRecurrenceApi.create(data);
            toast.success(i18n.t('ticketingAdmin:recurrence.toasts.created', { defaultValue: 'Recurring ticket schedule created' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(getIntegrationErrorMessage(err));
            return { success: false };
        }
    }
    async update(id, data) {
        try {
            const res = await ticketRecurrenceApi.update(id, data);
            toast.success(i18n.t('ticketingAdmin:recurrence.toasts.updated', { defaultValue: 'Updated' }));
            return { success: true, data: res.data };
        } catch (err) {
            toast.error(getIntegrationErrorMessage(err));
            return { success: false };
        }
    }
    async delete(id) {
        try {
            await ticketRecurrenceApi.delete(id);
            toast.success(i18n.t('ticketingAdmin:recurrence.toasts.deleted', { defaultValue: 'Deleted' }));
            return { success: true };
        } catch (err) {
            toast.error(getIntegrationErrorMessage(err));
            return { success: false };
        }
    }
}
